import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../../context/I18nContext';
import { Icons } from '../icons/Icons';
import useDropdown from '../../hooks/useDropdown';

const SheathSelector = ({ value, onChange, availableSheaths, onAddSheath, onDeleteSheath, className = '' }) => {
  const { t } = useI18n();
  const [showModal, setShowModal] = useState(false);
  const [newSheath, setNewSheath] = useState({
    diameter: '',
    length: '',
    type: 'PVC' // Default: PVC
  });

  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const { isOpen, toggleDropdown, closeDropdown } = useDropdown(false, {
    closeOnClickOutside: false
  });

  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 0 });

  const recalcMenuPosition = () => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    setMenuStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!isOpen) return;
    recalcMenuPosition();
    const onScroll = () => recalcMenuPosition();
    const onResize = () => recalcMenuPosition();
    const onDocMouseDown = (e) => {
      if (!dropdownRef.current || !menuRef.current) return;
      const inTrigger = dropdownRef.current.contains(e.target);
      const inMenu = menuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) {
        closeDropdown();
      }
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDocMouseDown, true);
    };
  }, [isOpen, closeDropdown]);

  const handleAddSheath = () => {
    if (!newSheath.diameter || !newSheath.length) {
      alert(t('cableForm.enterDiameterLength'));
      return;
    }

    const sheathObj = {
      id: Date.now(),
      diameter: parseFloat(newSheath.diameter),
      length: parseInt(newSheath.length),
      type: newSheath.type,
      label: `Ø${newSheath.diameter} - L${newSheath.length}`,
      fullLabel: `Guaina ${newSheath.type} Ø${newSheath.diameter} - L${newSheath.length}`
    };

    onAddSheath(sheathObj);
    setNewSheath({ diameter: '', length: '', type: 'PVC' });
    setShowModal(false);
    
    // Seleziona automaticamente la guaina appena creata
    onChange(sheathObj.id);
  };

  const handleSelectSheath = (sheathId) => {
    onChange(sheathId);
    closeDropdown();
  };

  const handleDeleteSheath = (e, sheathId) => {
    e.stopPropagation();
    if (window.confirm(t('cableForm.deleteSheathConfirm'))) {
      onDeleteSheath(sheathId);
      if (value === sheathId) {
        onChange(null);
      }
    }
  };

  const selectedSheath = availableSheaths.find(s => s.id === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown custom con + integrato */}
      <div className="relative">
        <button
          type="button"
          onClick={toggleDropdown}
          className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 relative min-h-[44px]"
        >
          <span className="text-gray-700">
            {selectedSheath ? (
              <>
                <span className="font-medium">{selectedSheath.type}</span>
                <span className="ml-2">{selectedSheath.label}</span>
              </>
            ) : (
              t('cableForm.noSheath')
            )}
          </span>

          {/* Freccetta (a destra) */}
          <svg
            className={`w-4 h-4 transition-transform flex-shrink-0 absolute right-3 top-1/2 -translate-y-1/2 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {/* Pulsante + (a sinistra della freccetta) - FUORI dal bottone principale */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowModal(true);
          }}
          className="absolute right-10 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded p-1 transition-colors z-10"
          title={t('cableForm.addNewSheath')}
        >
          <Icons.Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Dropdown menu via Portal per evitare clipping */}
      {isOpen && ReactDOM.createPortal(
        <div 
          ref={menuRef}
          className="fixed bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden"
          style={{ top: menuStyle.top, left: menuStyle.left, width: menuStyle.width, zIndex: 99999 }}
        >
          {/* Opzione "Nessuna guaina" */}
          <button
            type="button"
            onClick={() => handleSelectSheath(null)}
            className={`w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors ${!value ? 'bg-blue-100' : ''}`}
          >
            <span className="text-gray-600">{t('cableForm.noSheath')}</span>
          </button>

          {/* Lista guaine */}
          <div className="max-h-48 overflow-auto border-t border-gray-200">
            {availableSheaths.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                {t('cableForm.noSheathCreated')}
              </div>
            ) : (
              availableSheaths.map((sheath, index) => (
                <div
                  key={sheath.id}
                  className={`flex items-center justify-between hover:bg-blue-50 ${value === sheath.id ? 'bg-blue-100' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => handleSelectSheath(sheath.id)}
                    className="flex-1 px-3 py-2 text-left focus:outline-none"
                  >
                    <span className="font-medium text-gray-800">#{index + 1}</span>
                    <span className="ml-2 text-gray-700">{sheath.type}</span>
                    <span className="ml-2 text-gray-600">{sheath.label}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteSheath(e, sheath.id)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 transition-colors"
                    title={t('cableForm.deleteSheath')}
                  >
                    <Icons.Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Modale per aggiungere nuova guaina */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">{t('cableForm.addNewSheath')}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icons.X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cableForm.sheathType')}
                </label>
                <select
                  value={newSheath.type}
                  onChange={(e) => setNewSheath({ ...newSheath, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700"
                  autoFocus
                >
                  <option value="PVC">{t('cableForm.pvc')}</option>
                  <option value="Trecciata">{t('cableForm.braided')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cableForm.diameterMm')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={newSheath.diameter}
                  onChange={(e) => setNewSheath({ ...newSheath, diameter: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('cableForm.placeholderDiameter')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cableForm.lengthMm')}
                </label>
                <input
                  type="number"
                  value={newSheath.length}
                  onChange={(e) => setNewSheath({ ...newSheath, length: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('cableForm.placeholderLength')}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  {t('cableForm.sheathNote')}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleAddSheath}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  {t('cableForm.addSheathButton')}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {t('cableForm.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SheathSelector;
